import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AgentEditor from './components/AgentEditor';
import DealerList from './components/DealerList';
import Conversations from './components/Conversations';
import Inventory from './components/Inventory';
import Analytics from './components/Analytics';
import Login from './Login';
import { AuthProvider, useAuth } from './AuthContext';
import DealerFactory from './components/DealerFactory';
import Instructions from './components/Instructions';
import DealerUsers from './components/DealerUsers';
import Phenix from './components/Phenix';
import ADFConfig from './components/ADFConfig';
import MarketplaceSettings from './components/MarketplaceSettings';
import VmMonitoring from './components/VmMonitoring';
import ChatTest from './components/ChatTest';

const ProtectedRoute = ({ children }) => {
  const { isSignedIn, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }
  
  return isSignedIn ? children : <Navigate to="/login" replace />;
};

const Navigation = ({ navRef }) => {
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', label: 'Agents', component: 'agents' },
    { path: '/dealers', label: 'Dealer List (Legacy)', component: 'dealerList' },
    { path: '/dealer-factory', label: 'Dealer Factory (Legacy)', component: 'dealerFactory' },
    { path: '/conversations', label: 'Conversations', component: 'conversations' },
    { path: '/inventory', label: 'Inventory', component: 'inventory' },
    { path: '/instructions', label: 'Instructions', component: 'instructions' },
    { path: '/dealer-users', label: 'Dealer Users', component: 'dealerUsers' },
    { path: '/analytics', label: 'Analytics', component: 'analytics' },
    { path: '/phenix', label: 'Phenix (Legacy)', component: 'phenix' },
    { path: '/adf-config', label: 'ADF Config', component: 'adfConfig' },
    { path: '/marketplace-settings', label: 'Marketplace (Legacy)', component: 'marketplaceSettings' },
    { path: '/vm-monitoring', label: 'Marketplace VMs', component: 'vmMonitoring' },
    { path: '/test-chat', label: 'Test Chat', component: 'testChat' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <nav ref={navRef}>
      {navItems.map((item) => (
        <button
          key={item.path}
          onClick={() => handleNavigation(item.path)}
          className={location.pathname === item.path ? 'active' : ''}
        >
          {item.label}
        </button>
      ))}
      <button onClick={signOut}>Logout</button>
    </nav>
  );
};

function Dashboard() {
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(70);

  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return undefined;

    const updateNavHeight = () => {
      setNavHeight(navElement.getBoundingClientRect().height);
    };

    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateNavHeight);
      resizeObserver.observe(navElement);
    }

    return () => {
      window.removeEventListener('resize', updateNavHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="App" style={{ paddingTop: `${navHeight + 20}px` }}>
      <Navigation navRef={navRef} />
      <Routes>
        <Route path="/" element={<AgentEditor />} />
        <Route path="/agents" element={<Navigate to="/" replace />} />
        <Route path="/dealers" element={<DealerList />} />
        <Route path="/dealer-factory" element={<DealerFactory />} />
        <Route path="/conversations" element={<Conversations />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/instructions" element={<Instructions />} />
        <Route path="/dealer-users" element={<DealerUsers />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/phenix" element={<Phenix />} />
        <Route path="/adf-config" element={<ADFConfig />} />
        <Route path="/marketplace-settings" element={<MarketplaceSettings />} />
        <Route path="/vm-monitoring" element={<VmMonitoring />} />
        <Route path="/test-chat" element={<ChatTest />} />
        {/* Deprecated routes */}
        <Route path="/assistants" element={<Navigate to="/" replace />} />
        <Route path="/create-capture" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
